import {
  Button,
  ButtonGroup,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';

import type { AssignmentFormData } from '@/client/components/admin/assignments/types/assignmentTypes';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import { useAdminAssignmentsStore } from '../state/adminAssignmentsStore';
import { useCreateAndUpdateWorkflow } from '../builder/hooks/useCreateAndUpdateWorkflow';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTriggerPointInTimeWorkflow } from '../builder/hooks/useTriggerPointInTimeWorkflow';
import { useUpdateWorkflowResultGroup } from '../builder/hooks/useUpdateWorkflowResultGroup';

interface WorkflowPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WorkflowPublishModal({ isOpen, onClose }: WorkflowPublishModalProps) {
  const { t } = useTranslation();
  const { watch } = useFormContext<AssignmentFormData>();
  const { createWorkflowMutation } = useCreateAndUpdateWorkflow();
  const { status: createWorkflowStatus, variables } = createWorkflowMutation;
  const { triggerPointInTimeWorkflowMutation } = useTriggerPointInTimeWorkflow();
  const { setCanLeave } = useAdminAssignmentsStore();
  const {
    status: triggerPointInTimeWorkflowStatus,
    variables: triggerPointInTimeWorkflowVariables,
  } = triggerPointInTimeWorkflowMutation;
  const { updateWorkflowResultGroupMutation } = useUpdateWorkflowResultGroup();
  const { status: updateWorkflowResultGroupStatus, variables: updateWorkflowResultGroupVariables } =
    updateWorkflowResultGroupMutation;
  const location = useLocation();
  const { pathname } = location;

  const { settings } = watch();

  const { type } = settings;

  const isPointInTime = type === WorkflowType.PointInTime;
  const isInstanceCreation = pathname.includes('instance') && pathname.includes('create');
  const isInstanceEdit = pathname.includes('instance') && pathname.includes('edit');

  const isLoading =
    createWorkflowStatus === 'loading' ||
    triggerPointInTimeWorkflowStatus === 'loading' ||
    updateWorkflowResultGroupStatus === 'loading';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />

      <ModalContent paddingTop={12} paddingX={10} paddingBottom={8}>
        <ModalBody>
          <VStack spacing={4} marginBottom={10} textAlign="center">
            <Image
              src="/public/images/Message-Sent-1--Streamline-Brooklyn.svg"
              alt="Publish Workflow"
            />

            <Text fontSize="20px" fontWeight={600}>
              {isInstanceEdit
                ? t('admin.assignments.publish.instance.confirm')
                : isPointInTime
                ? t('admin.assignments.publish.pointInTime.confirm')
                : t('admin.assignments.publish.automated.confirm')}
            </Text>

            <Text fontSize="14px" fontWeight={500}>
              {isInstanceEdit
                ? t('admin.assignments.publish.instance.confirm.helpText')
                : isPointInTime
                ? t('admin.assignments.publish.pointInTime.confirm.helpText')
                : t('admin.assignments.publish.automated.confirm.helpText')}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup width="full" justifyContent="center" gap={4}>
            <Button variant="createCancel" onClick={onClose}>
              {t('global.actions.cancel')}
            </Button>

            {!isPointInTime && (
              <OutlineButton
                isLoading={isLoading}
                onClick={() => {
                  setCanLeave(true);
                  createWorkflowMutation.mutateAsync({ enabled: false });
                }}
              >
                {t('admin.assignments.publish.publishOnly')}
              </OutlineButton>
            )}

            <SolidButton
              isLoading={isLoading}
              onClick={() => {
                setCanLeave(true);

                isInstanceCreation
                  ? triggerPointInTimeWorkflowMutation.mutateAsync()
                  : isInstanceEdit
                  ? updateWorkflowResultGroupMutation.mutateAsync()
                  : createWorkflowMutation.mutateAsync({ enabled: true });
              }}
            >
              {isPointInTime
                ? t('admin.assignments.publish')
                : t('admin.assignments.publish.publishAndActivate')}
            </SolidButton>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
